'use client';

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import SunCalc from 'suncalc';
import { Sun, Moon } from 'lucide-react';

const LAT = 67.5;
const LON = 24.5;

interface DaylightClockProps {
  compact?: boolean;
  className?: string;
  /** Copy overrides (editable/translatable via the guest-guide manifest). */
  title?: string;
  polarNightLabel?: string;
  polarNightSub?: string;
  midnightSunLabel?: string;
  midnightSunSub?: string;
  daylightSuffix?: string;
  sunriseLabel?: string;
  sunsetLabel?: string;
}

export default function DaylightClock({
  compact = false,
  className,
  title = 'Daylight in Lapland',
  polarNightLabel = 'Polar Night',
  polarNightSub = "The sun doesn't rise today",
  midnightSunLabel = 'Midnight Sun',
  midnightSunSub = "The sun doesn't set today!",
  daylightSuffix = 'of daylight today',
  sunriseLabel = 'Sunrise',
  sunsetLabel = 'Sunset',
}: DaylightClockProps) {
  const data = useMemo(() => {
    const now = new Date();
    const times = SunCalc.getTimes(now, LAT, LON);
    const sunrise = times.sunrise;
    const sunset = times.sunset;

    const sunriseValid = !isNaN(sunrise.getTime()) && sunrise.getTime() !== sunset.getTime();
    const sunsetValid = !isNaN(sunset.getTime());

    // Check for polar night / midnight sun
    const noonAlt = SunCalc.getPosition(times.solarNoon, LAT, LON).altitude;
    const midnightDate = new Date(now);
    midnightDate.setHours(0, 0, 0, 0);
    const midnightAlt = SunCalc.getPosition(midnightDate, LAT, LON).altitude;

    const isPolarNight = noonAlt < 0;
    const isMidnightSun = midnightAlt > 0;

    let daylightHours = 0;
    let daylightMinutes = 0;
    let sunriseStr = '';
    let sunsetStr = '';

    if (isPolarNight) {
      daylightHours = 0;
      daylightMinutes = 0;
      sunriseStr = '-';
      sunsetStr = '-';
    } else if (isMidnightSun) {
      daylightHours = 24;
      daylightMinutes = 0;
      sunriseStr = '-';
      sunsetStr = '-';
    } else if (sunriseValid && sunsetValid) {
      const diffMs = sunset.getTime() - sunrise.getTime();
      const totalMinutes = Math.round(diffMs / 60000);
      daylightHours = Math.floor(totalMinutes / 60);
      daylightMinutes = totalMinutes % 60;
      sunriseStr = sunrise.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Helsinki' });
      sunsetStr = sunset.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Helsinki' });
    }

    // Sun arc for visual (0-1 scale of day progress)
    const nowMs = now.getTime();
    let sunProgress = 0.5;
    if (!isPolarNight && !isMidnightSun && sunriseValid && sunsetValid) {
      const dayLength = sunset.getTime() - sunrise.getTime();
      sunProgress = Math.max(0, Math.min(1, (nowMs - sunrise.getTime()) / dayLength));
    }

    return { daylightHours, daylightMinutes, sunriseStr, sunsetStr, isPolarNight, isMidnightSun, sunProgress };
  }, []);

  if (compact) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        {data.isPolarNight ? (
          <Moon className="w-4 h-4 text-[#9B7B3A]" />
        ) : (
          <Sun className="w-4 h-4 text-[#C4A95E]" />
        )}
        <span className={cn('text-sm font-medium', 'text-[#1A1A2E]')}>
          {data.isPolarNight
            ? 'Polar night - 0h daylight'
            : data.isMidnightSun
              ? 'Midnight sun - 24h daylight'
              : `${data.daylightHours}h ${data.daylightMinutes}m daylight`}
        </span>
      </div>
    );
  }

  const arcAngle = (data.daylightHours + data.daylightMinutes / 60) / 24;

  return (
    <div className={cn('rounded-2xl p-6', 'bg-white shadow-sm', className)}>
      <h3 className={cn('font-sans text-lg font-light mb-4', 'text-[#1A1A2E]')}>
        {title}
      </h3>

      {/* Arc visualization */}
      <div className="relative w-full max-w-[200px] mx-auto mb-4">
        <svg viewBox="0 0 200 120" className="w-full">
          {/* Background arc */}
          <path
            d="M 20 110 A 80 80 0 0 1 180 110"
            fill="none"
            stroke="#F0EDE5"
            strokeWidth="12"
            strokeLinecap="round"
          />
          {/* Daylight arc */}
          <path
            d="M 20 110 A 80 80 0 0 1 180 110"
            fill="none"
            stroke={data.isPolarNight ? '#9B7B3A' : '#C4A95E'}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={`${arcAngle * 251.3} 251.3`}
          />
          {/* Sun/Moon icon position */}
          {!data.isPolarNight && !data.isMidnightSun && (
            <circle
              cx={20 + data.sunProgress * 160}
              cy={110 - Math.sin(data.sunProgress * Math.PI) * 80}
              r="8"
              fill="#C4A95E"
            />
          )}
        </svg>
      </div>

      {/* Big number */}
      <div className="text-center mb-3">
        {data.isPolarNight ? (
          <>
            <p className="text-3xl font-sans font-bold text-[#9B7B3A]">{polarNightLabel}</p>
            <p className={cn('text-sm mt-1', 'text-[#5A5A6E]')}>
              {polarNightSub}
            </p>
          </>
        ) : data.isMidnightSun ? (
          <>
            <p className="text-3xl font-sans font-bold text-[#C4A95E]">{midnightSunLabel}</p>
            <p className={cn('text-sm mt-1', 'text-[#5A5A6E]')}>
              {midnightSunSub}
            </p>
          </>
        ) : (
          <>
            <p className={cn('text-3xl font-sans font-bold', 'text-[#1A1A2E]')}>
              {data.daylightHours}h {data.daylightMinutes}m
            </p>
            <p className={cn('text-sm mt-1', 'text-[#5A5A6E]')}>
              {daylightSuffix}
            </p>
          </>
        )}
      </div>

      {/* Sunrise / Sunset */}
      {!data.isPolarNight && !data.isMidnightSun && (
        <div className="flex justify-between text-sm">
          <span className="text-[#5A5A6E]">
            {sunriseLabel} {data.sunriseStr}
          </span>
          <span className="text-[#5A5A6E]">
            {sunsetLabel} {data.sunsetStr}
          </span>
        </div>
      )}
    </div>
  );
}
